import React, { useMemo, useCallback } from 'react'
import styled from 'styled-components'
import cn from 'clsx'
import { rem } from 'polished'
import Clickable from 'components/Common/Clickable'
import { pluralizeToString } from 'lib/string/pluralize'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import FormatCurrency from 'components/Common/FormatCurrency'
import TextLink from 'LuxKit/TextLink'
import SolidStarCircleIcon from 'LuxKit/Icons/solid/SolidStarCircleIcon'
import SolidFireIcon from 'LuxKit/Icons/solid/SolidFireIcon'
import { displayPricingAs, getCalendarDisplayTotalPrice } from 'lib/hotels/bedbankUtils'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import Group from 'components/utils/Group'

const CellWrapper = styled(Clickable)`
  display: flex;
  flex-direction: column;
  padding: ${rem(4)} ${rem(8)};
  transition: background-color 0.2s;
  min-height: ${rem(72)};

  box-shadow: 0 -1px 0 0 ${props => props.theme.palette.neutral.default.five},
  1px 0 0 0 ${props => props.theme.palette.neutral.default.five},
  1px 0 0 0 ${props => props.theme.palette.neutral.default.five} inset,
  0 -1px 0 0 ${props => props.theme.palette.neutral.default.five} inset;

  &:not(.check-in):not(.check-out):not(:disabled):not(.other-availability) {
    ${mediaHoverable} {
      &:hover {
        background-color: ${props => props.theme.palette.highlight.primary.lightBackground};
      }
    }
    &:focus {
      background-color: ${props => props.theme.palette.highlight.primary.lightBackground};
    }
  }

  &:disabled, &.other-availability {
    background-color: ${props => props.theme.palette.neutral.default.seven};
  }

  &.check-in, &.check-out {
    background-color: ${props => props.theme.palette.highlight.primary.normalBackground};
  }

  &.between-day {
    background-color: ${props => props.theme.palette.highlight.primary.lightBackground};
  }

  &.sold-out-background {
    background-color: ${props => props.theme.palette.neutral.default.seven};
  }
`

const Main = styled.div`
  opacity: 1;
  pointer-events: auto;
  transition: opacity 0.2s;
  display: flex;
  flex-direction: column;

  &.hidden {
    opacity: 0;
    pointer-events: none;
  }
`

interface Props {
  isCheckIn?: boolean;
  isCheckOut?: boolean;
  duration: number;
  day: number;
  hasPromo: boolean;
  isCorporate: boolean;
  price?: number;
  propertyFees?: number;
  flightPrice?: number;
  checkIn: string;
  style?: React.CSSProperties;
  className?: string;
  isBetweenDay?: boolean;
  fetching: boolean;
  flights: boolean;
  onClick: () => void;
  enableCalendarRecommendations: boolean;
  adultsNumber: number;
  roomsNumber: number;
  displayPricingAsPerPerson: boolean;
}

function CalendarDayCell(props: Props) {
  const {
    duration,
    day,
    price,
    propertyFees,
    isCheckIn,
    isCheckOut,
    style,
    className,
    isBetweenDay,
    hasPromo,
    isCorporate,
    fetching,
    flights,
    flightPrice,
    onClick,
    enableCalendarRecommendations,
    checkIn,
    adultsNumber,
    roomsNumber,
    displayPricingAsPerPerson,
  } = props
  const dispatch = useOfferPageDispatch()
  const colour = (isCheckIn || isCheckOut) ? 'neutral-one' : undefined

  const soldOut = hasPromo ? !price : false

  const display = useMemo(() => {
    const cellSelection = {
      isBetweenDay,
      isCheckOut,
      isCheckIn,
      isNone: !isBetweenDay && !isCheckIn && !isCheckOut,
      shouldHideMainInformation: isBetweenDay || isCheckOut,
      showRecommendationLink: !fetching && soldOut && enableCalendarRecommendations,
    }

    return {
      cellSelection,
    }
  }, [fetching, soldOut, isBetweenDay, isCheckIn, isCheckOut, enableCalendarRecommendations])

  const onCellClick = useCallback(() => {
    if (isCheckIn) { return }
    if (display.cellSelection.showRecommendationLink) {
      dispatch({ type: OfferPageActions.showRecommendationsView, checkInDay: checkIn })
      return
    }
    onClick?.()
  }, [onClick, isCheckIn, dispatch, checkIn, display.cellSelection.showRecommendationLink])

  const hotelPrice = price ?? 0
  const hotelPropertyFees = propertyFees ?? 0

  const {
    total,
    saleUnit,
  } = getCalendarDisplayTotalPrice({
    price: hotelPrice + hotelPropertyFees,
    adultsNumber,
    roomsNumber,
    withFlights: !!flights,
    flightPrice: flightPrice ?? 0,
    nights: duration,
    displayPricingPer: displayPricingAs(displayPricingAsPerPerson),
  })

  return (
    <CellWrapper
      style={style}
      className={cn(className, {
        'check-in': display.cellSelection.isCheckIn,
        'check-out': display.cellSelection.isCheckOut,
        'between-day': display.cellSelection.isBetweenDay,
        'sold-out-background': soldOut && display.cellSelection.isNone,
      })}
      disabled={soldOut && !(display.cellSelection.showRecommendationLink && display.cellSelection.isNone)}
      onClick={onCellClick}
    >
      <Group direction="horizontal" horizontalAlign="space-between">
        <Heading colour={colour} as="span" variant="heading6">{day}</Heading>
        <BodyText colour="neutral-one" as="span" variant="small" weight="bold">
          {display.cellSelection.isCheckIn && 'Check in'}
          {display.cellSelection.isCheckOut && 'Check out'}
        </BodyText>
        <div>
          {isCorporate && !soldOut && !display.cellSelection.isBetweenDay && <SolidStarCircleIcon size="S" colour={!colour ? 'highlight-secondary' : undefined} />}
          {hasPromo && !soldOut && !display.cellSelection.isBetweenDay && <SolidFireIcon size="XS" colour={!colour ? 'urgency' : undefined} />}
        </div>
      </Group>

      <Main className={cn({ hidden: display.cellSelection.shouldHideMainInformation })}>
        {!soldOut && hasPromo && <>
          <Caption colour={colour ?? 'neutral-three'} variant="medium">
            {pluralizeToString('night', duration)} {flights && flightPrice && '+ flights '} {' from'}
          </Caption>
          <div>
            <BodyText as="span" variant="medium" weight="bold">
              <FormatCurrency value={total} />
              {displayPricingAsPerPerson && <Caption variant="medium" as="span">{saleUnit}</Caption>}
            </BodyText>
            {!displayPricingAsPerPerson && <Caption colour={colour} as="span" variant="medium"> {saleUnit}</Caption>}
            {flights && !flightPrice && <Caption colour={colour} as="span" variant="medium"> + flights</Caption>}
          </div>
        </>}

        {!hasPromo && !display.cellSelection.isCheckIn && <Caption variant="large" colour={colour ?? 'neutral-three'} underline>Select to view prices</Caption>}
        {soldOut && !fetching && <Caption variant="large" colour={colour ?? 'neutral-three'}>Sold out</Caption>}
        {display.cellSelection.showRecommendationLink && <TextLink size="small">View similar offers</TextLink>}
      </Main>
    </CellWrapper>
  )
}

export default React.memo(CalendarDayCell)
